/* eslint-disable max-statements */
import { Grid, LoadingOverlay, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useState } from 'react';

import { TSDType } from '@e-origin/shared';

import { AsyncAutocomplete, MultiLangCodes, TextInput } from '@components';
import { shouldShowForTypes } from '@features/tsd/utils';
import { useDeclarant, useGoodLocation } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { TSDViewGeneralInfoFormValues } from './validateTSDViewGeneralInfoForm';

interface TSDLocationSectionProps {
  form: UseFormReturnType<TSDViewGeneralInfoFormValues>;
  tsdType: TSDType;
  disabled: boolean;
}

export const TSDLocationSection: React.FC<TSDLocationSectionProps> = ({ form, tsdType, disabled }) => {
  const codelistSystems = useCodeListSystem({});
  const { data: declarant } = useDeclarant();
  const { data: { data: goodsLocations } = {}, isLoading } = useGoodLocation(declarant._id);
  const [unlocodes, setUnlocodes] = useState([]);
  const [qualifierOfIdentification, setQualifierOfIdentification] = useState('');

  const handleUNLocodeChange = (value: string | null) => {
    const selectedGoodsLocation = goodsLocations.find((code) => code.unLocode === value);

    form.setFieldValue('locationOfGoods', {
      ...form.values.locationOfGoods,
      UNLocode: value ?? '',
      typeOfLocation: selectedGoodsLocation?.type || '',
      qualifierOfIdentification: selectedGoodsLocation?.qualifierOfIdentification || '',
      authorisationNumber: selectedGoodsLocation?.authorisationNumber || '',
      additionalIdentifier: selectedGoodsLocation?.additionalIdentifier || '',
      customsOffice: {
        referenceNumber: selectedGoodsLocation?.customsOffice?.referenceNumber || '',
      },
      gnss: {
        latitude: `${selectedGoodsLocation?.gnss?.latitude || ''}`,
        longitude: `${selectedGoodsLocation?.gnss?.longitude || ''}`,
      },
      economicOperator: {
        identificationNumber: selectedGoodsLocation?.economicOperator?.identificationNumber || '',
      },
      address: {
        streetAndNumber: selectedGoodsLocation?.address?.street || '',
        postcode: selectedGoodsLocation?.address?.postcode || '',
        city: selectedGoodsLocation?.address?.city || '',
        country: selectedGoodsLocation?.address?.country || '',
      },
    });
  };

  useEffect(() => {
    if (goodsLocations?.length) {
      setUnlocodes(goodsLocations.map((loc) => ({ label: loc.unLocode, value: loc.unLocode })));
    }
  }, [goodsLocations]);

  useEffect(() => {
    setQualifierOfIdentification(form.values.locationOfGoods.qualifierOfIdentification);
  }, [form.values.locationOfGoods.qualifierOfIdentification]);

  if (!goodsLocations) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack mb="xl">
      <Title order={5} fw={600}>
        Location of Goods
      </Title>

      <Grid>
        <Grid.Col span={3}>
          <AsyncAutocomplete
            {...form.getInputProps('locationOfGoods.UNLocode')}
            label="UNLOCODE"
            fetchOptions={(search: string) => unlocodes.filter((code) => code.value.includes(search)) as any}
            onChange={(value) => handleUNLocodeChange(value)}
            disabled={disabled}
          />
        </Grid.Col>

        <Grid.Col span={2}>
          <MultiLangCodes
            {...form.getInputProps('locationOfGoods.typeOfLocation')}
            label="Location type"
            system={codelistSystems}
            code="CL347"
            disabled={disabled}
          />
        </Grid.Col>

        <Grid.Col span={2}>
          <MultiLangCodes
            {...form.getInputProps('locationOfGoods.qualifierOfIdentification')}
            label="Location qualifier"
            system={codelistSystems}
            code="CL326"
            disabled={disabled}
          />
        </Grid.Col>

        <Grid.Col span={2.5}>
          <TextInput {...form.getInputProps('locationOfGoods.authorisationNumber')} label="Authorisation number" disabled={disabled} />
        </Grid.Col>

        <Grid.Col span={2.5}>
          <TextInput {...form.getInputProps('locationOfGoods.additionalIdentifier')} label="Additional identifier" disabled={disabled} />
        </Grid.Col>
      </Grid>

      {qualifierOfIdentification === 'V' && (
        <Stack gap="xs">
          <Title order={6} fw={600}>
            Customs office
          </Title>

          <Grid>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('locationOfGoods.customsOffice.referenceNumber')} label="Customs office reference" disabled={disabled} />
            </Grid.Col>
          </Grid>
        </Stack>
      )}

      {qualifierOfIdentification === 'W' && (
        <Stack gap="xs">
          <Title order={6} fw={600}>
            GNSS coordinates
          </Title>

          <Grid>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('locationOfGoods.gnss.latitude')} label="Latitude" disabled={disabled} />
            </Grid.Col>

            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('locationOfGoods.gnss.longitude')} label="Longitude" disabled={disabled} />
            </Grid.Col>
          </Grid>
        </Stack>
      )}

      {qualifierOfIdentification === 'X' && (
        <Stack gap="xs">
          <Title order={6} fw={600}>
            Economic operator
          </Title>

          <Grid>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('locationOfGoods.economicOperator.identificationNumber')} label="EORI number" disabled={disabled} />
            </Grid.Col>
          </Grid>
        </Stack>
      )}

      {['Z', 'T'].includes(qualifierOfIdentification) && (
        <Stack gap="xs">
          <Title order={6} fw={600}>
            Address
          </Title>

          <Grid>
            <Grid.Col span={4}>
              <TextInput {...form.getInputProps('locationOfGoods.address.streetAndNumber')} label="Street and number" disabled={disabled} />
            </Grid.Col>

            <Grid.Col span={3}>
              <MultiLangCodes
                {...form.getInputProps('locationOfGoods.address.country')}
                label="Country"
                system={codelistSystems}
                code="CL199"
                disabled={disabled}
              />
            </Grid.Col>

            <Grid.Col span={2}>
              <TextInput {...form.getInputProps('locationOfGoods.address.postcode')} label="Post code" disabled={disabled} />
            </Grid.Col>

            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('locationOfGoods.address.city')} label="City" disabled={disabled} />
            </Grid.Col>
          </Grid>
        </Stack>
      )}
    </Stack>
  );
};
